/**
 * 路由权限配置文件
 * 所有的路由权限控制都应该在这里配置。
 * 菜单中取此处权限结合自定义的条件决定显示条件
 */
import { hasAbilityByOr, hasCurAbility, matchJsonArrayOr } from '@pro/common/dao/company-ability';
import storage from '@pro/common/dao/storage';
import { permissionInstantce, grayscaleFunctionsInstantce } from './user-permission';
/**
 * 检查权限是否初始化，没有初始化抛出错误
 */
const checkThrowError = (val) => {
  // eslint-disable-next-line no-undefined
  if (val === undefined) {
    throw new Error('MONITOR_IGNORE权限未初始化');
  }
  return val;
};

/**
 * 所有权限依赖项
 * 这些依赖都必须从localstorage中读取，保证整个页面权限都是一致的
 * store中的值也需要在赋值时转存到localstorage中
 */
let lib = {
  get userInfo() {
    return checkThrowError(storage.get(storage.NAME.USER));
  },
  get epPermissions() {
    return lib.userInfo.epSessionData?.permission || [];
  },
  get userPermission() {
    return checkThrowError(permissionInstantce);
  },
  get grayscaleFunctions() {
    return checkThrowError(grayscaleFunctionsInstantce);
  },
  get epAuthList() {
    return checkThrowError(storage.get(storage.NAME.EP_AUTH_LIST));
  },
  // 是否游客模式
  get touristMode() {
    return storage.realGet(storage.NAME.TOURIST_MODE) === 'true';
  },
  // 账号下是否有设备
  get hasDevice() {
    return checkThrowError(storage.realGetByUser('hasDevice'));
  },
  get installerSecondAuth() {
    return checkThrowError(storage.get(storage.NAME.INSTALLER_SECOND_AUTH));
  },
  // 积分权限未初始化时则视为没有权限
  // 因为如果是经菜单跳转的话，菜单显示了则表示权限已经初始化了，这里就不需要验证是否初始化
  get supportPointSystem() {
    return storage.realGetByUser('supportPointSystem');
  },
  get vmsAbility() {
    return storage.realGetByUser('vmsAbility');
  },
  get simAbility() {
    return storage.realGetByUser('simAbility');
  },
  get isIndiaSupportAbility() {
    return hasCurAbility(143);
  },
  //获取ep国家所有的配置
  get getEpCountrySettings() {
    return checkThrowError(storage.get('epConuntrySettings'));
  },
  get checkAppModeNotNeutral() {
    return process.env.appmode === 'NORMAL';
  },
  //判断用户是否为公司法人
  get isLegal() {
    return lib.userInfo.juridicalPersonFlag;
  },
  get isAzureLogin() {
    return lib.userInfo?.loginType === 1;
  },
  get isItally() {
    return lib.userInfo?.country === '10';
  }
};

/**
 * 附加前置或者后置权限鉴定
 * @param {(() => boolean) | undefined} prefix
 * @param {import('../declarations/page-permission').PagePermissions} pagePermissions
 * @param {(() => boolean) | undefined} suffix
 * @returns {import('../declarations/page-permission').PagePermissions}
 */
const attachPermission = (prefix, pagePermissions, suffix) => {
  if (!prefix && !suffix) {
    return pagePermissions;
  }

  return pagePermissions.map((item) => {
    const getPermission = item.getPermission;
    item.getPermission = () => {
      if (prefix && !prefix()) {
        return false;
      }

      let result = true;
      if (getPermission) {
        result = getPermission();
        if (!result) {
          return false;
        }
      }

      if (suffix && !suffix()) {
        return false;
      }
      return result;
    };
    return item;
  });
};

/**
 * 不需要登录的公开路由列表
 * @type {import('../declarations/page-permission').PagePermissions}
 */
export const PUBLIC = [
  // 重定向页面，还未进入权限鉴定就已被转走，无需控制
  // ...REDIRECT_PATH,
  { path: '/' },
  { path: '/404' },
  { path: '/EPartner/ExploreChannel' },
  { path: '/marketing/unsubscribe' },
  { path: '/cookiePolicy' },
  { path: '/EP2FuncPage' },
  { path: '/statement/*' },
  { path: '/portalAuth' },
  { path: '/compatibilityonline' },
  { path: '/trustAgree' },
  // 工单页的鉴权由业务代码自行实现
  { path: '/support/*' }
];

/**
 * 登录后页面
 *
 * `getPermission`的返回值类型可以是boolean、string、null、undefined，
 * 如果返回的是字符串，将在鉴定权限后重定向到字符串指定的页面
 * @type {import('../declarations/page-permission').PagePermissions}
 */
export const AUTHED = [
  // 不在菜单中的路由
  ...[{ path: '/MicroApp/about' }],
  // Home
  ...attachPermission(
    () => lib.userInfo.whiteListUser === 1,
    [
      {
        path: '/installer/basic/home'
      }
    ]
  ),
  // Site
  ...attachPermission(
    () =>
      (lib.userInfo.whiteListUser === 1 && !lib.userInfo.epSapId)
      || (lib.userInfo.whiteListUser === 1
        && lib.userInfo.epSapId
        && (lib.hasDevice || (!lib.hasDevice && lib.epPermissions.includes('epartner.hikproconnect')))),
    [
      { path: '/installer/basic/paymentDialogPage' },
      { path: '/installer/basic/transferPage' },
      // siteMenuList
      {
        path: '/installer/basic/site'
      },
      {
        // 'site/detail/:id'、'site/siteReportSetting'、'site/batchArming'
        // 'site/detail/transferDevice/:id'、'site/detail/transferDeviceDetail/:id'
        path: '/installer/basic/site/**'
      },
      {
        path: '/installer/basic/siteMap',
        getPermission: () => lib.userPermission.siteManage || lib.userPermission.siteAll
      },
      {
        path: '/installer/basic/vms',
        getPermission: () => lib.vmsAbility && lib.userPermission.siteAll && lib.checkAppModeNotNeutral
      },
      {
        // 'vms/detail/:id'
        path: '/installer/basic/vms/**',
        getPermission: () => lib.vmsAbility && lib.userPermission.siteAll
      },
      {
        path: '/installer/basic/sim',
        getPermission: () => lib.simAbility
      },
      // configList
      {
        path: '/installer/basic/batchConfigureDevices',
        getPermission: () => !lib.touristMode && window.SUPPORT_LAN_BATCHCONFIG
      },
      {
        path: '/installer/basic/batchConfigureRemote',
        getPermission: () => !lib.touristMode && hasAbilityByOr([48, 49])
      },
      {
        path: '/installer/basic/batchArmDisarm',
        getPermission: () => !lib.touristMode && hasAbilityByOr([46, 47]) && lib.checkAppModeNotNeutral // 重启灰度控制，控制美国
      },
      {
        path: '/installer/basic/batchUpgradesOnLan',
        getPermission: () => !lib.touristMode && window.SUPPORT_LAN_BATCHCONFIG
      },
      // Handover Template
      {
        path: '/installer/basic/handoverTemplate',      //交付模板
        getPermission: () => lib.userPermission.siteAll && hasAbilityByOr({ value: '1' }, [250, 251])    //用户拥有Manage All Sites权限
      },
      // healthCheckList
      { path: '/installer/basic/healthCheck' },
      { path: '/installer/basic/healthCheck/allSite' },
      { path: '/installer/basic/healthCheck/site/*' },
      {
        path: '/installer/basic/reportSetting',
        getPermission: () => !lib.touristMode
      },
      {
        path: '/installer/basic/quotation',
        getPermission: () =>
          !lib.touristMode && hasAbilityByOr({ value: '1' }, [137, 138]) && lib.isItally
      },
      // serviceList
      {
        path: '/installer/basic/packageMarket',
        getPermission: () => lib.userPermission.pureBill && lib.userPermission.showInFree
      },
      {
        path: '/installer/basic/packageManager',
        getPermission: () => lib.userPermission.pureBill && lib.userPermission.siteAll
      },
      {
        path: '/installer/basic/costCenter',
        getPermission: () =>
          lib.userPermission.auth
          && lib.userPermission.pureBill
          && lib.userPermission.showInFree
          && lib.userInfo.payMethod !== '-1'
      },
      {
        path: '/installer/basic/costCenter/*',
        getPermission: () =>
          lib.userPermission.auth
          && lib.userPermission.pureBill
          && lib.userPermission.showInFree
          && lib.userInfo.payMethod !== '-1'
      },
      {
        path: '/installer/basic/arcManagement',
        getPermission: () => !lib.touristMode && hasAbilityByOr({ value: '1' }, [200, 201]) && lib.userPermission.arcManagerPermission && lib.checkAppModeNotNeutral
      },
      {
        path: '/installer/basic/arcReceive',
        getPermission: () => !lib.touristMode && hasAbilityByOr({ value: '1' }, [236, 237]) && lib.userPermission.arcManagerPermission && lib.checkAppModeNotNeutral
      }
    ]
  ),
  // 异常中心（设备异常及报警事件）
  { path: '/installer/basic/messages', getPermission: () => lib.userInfo.whiteListUser === 1 },
  // 消息
  { path: '/installer/basic/info', getPermission: () => lib.userInfo.whiteListUser === 1 },
  // Product
  ...attachPermission(
    () => lib.epPermissions.includes('epartner.product.public'),
    [{ path: '/Product' }, { path: '/Product/**' }]
  ),
  // Explore
  ...attachPermission(
    // 47
    () =>
      (lib.epPermissions.includes('epartner.basic') || lib.userInfo.originType === 1)
      && hasAbilityByOr({ value: '1' }, [78, 80])
      && lib.userInfo.whiteListUser === 1,
    [
      { path: '/Explore/list/feeds' },
      {
        path: '/Explore/list/event',
        getPermission: () => hasAbilityByOr({ value: '1' }, [95, 96])
      },
      { path: '/Explore/list/howto' },
      { path: '/Explore/list/video' },
      { path: '/Explore/notice' }
    ]
  ),
  // Solution
  ...attachPermission(
    () => lib.epPermissions.includes('epartner.solution'),
    [{ path: '/Solution' }, { path: '/Solution/**' }]
  ),
  // Project
  ...attachPermission(
    () => lib.epPermissions.includes('epartner.project'),
    [{ path: '/Project' }, { path: '/Project/**' }]
  ),
  // Incentive
  ...attachPermission(
    () => !!lib.userInfo.oneHikId && lib.epPermissions.includes('epartner.incentive'),
    [
      {
        path: '/Incentive/pointsMall/index',
        getPermission: () => lib.supportPointSystem && lib.userInfo.upgradeFlag
      },
      {
        path: '/Incentive/partnerProgram/index/myProgram?source=menu',
        getPermission: () => lib.epAuthList.dppSignEntrance === 1 || lib.epAuthList.dppScanEntrance
      },
      {
        path: '/Incentive/compensation/index/wait',
        getPermission: () => lib.epPermissions.includes('epartner.compensation')
      },
      {
        path: '/Incentive/myAgreement',
        getPermission: () => lib.epAuthList.dppSignEntrance === 2
      },
      {
        path: '/Incentive/stockManagement/index',
        getPermission: () => lib.epPermissions.includes('epartner.stockManagement')
      }
    ]
  ),
  // 旧积分
  {
    path: '/installer/basic/pointsSystem',
    getPermission: () => lib.supportPointSystem && !lib.userInfo.upgradeFlag
  },
  // Support
  ...attachPermission(
    () => lib.epPermissions.includes('epartner.basic') || lib.isIndiaSupportAbility,
    [
      {
        path: '/installer/basic/tools',
        getPermission: () =>
          lib.isIndiaSupportAbility
            ? hasAbilityByOr({ value: 0 }, [143], matchJsonArrayOr)
            : lib.userInfo.whiteListUser === 1
      },
      {
        path: '/installer/basic/teachingCenter',
        getPermission: () =>
          lib.isIndiaSupportAbility
            ? hasAbilityByOr({ value: 1 }, [143], matchJsonArrayOr)
            : lib.checkAppModeNotNeutral && !lib.touristMode && lib.userInfo.whiteListUser === 1
      },
      {
        path: '/Support/suggestion',
        getPermission: () =>
          lib.isIndiaSupportAbility
            ? hasAbilityByOr({ value: 4 }, [143], matchJsonArrayOr)
            : !!lib.userInfo.oneHikId && lib.userInfo.whiteListUser === 1
      },
      {
        path: '/Support/serialNumber',
        getPermission: () =>
          lib.isIndiaSupportAbility
            ? hasAbilityByOr({ value: 5 }, [143], matchJsonArrayOr)
            : lib.checkAppModeNotNeutral
            && lib.epPermissions.includes('epartner.rma.inquiry')
            && lib.userInfo.whiteListUser === 1
      },
      {
        path: '/Support/repair',
        getPermission: () =>
          lib.isIndiaSupportAbility
            ? hasAbilityByOr({ value: 6 }, [143], matchJsonArrayOr)
            : lib.checkAppModeNotNeutral
            && lib.epPermissions.includes('epartner.rma.distributor.application')
            && lib.userInfo.whiteListUser === 1
      },
      {
        path: '/Support/contact',
        getPermission: () =>
          lib.isIndiaSupportAbility
            ? hasAbilityByOr({ value: 7 }, [143], matchJsonArrayOr)
            : !!lib.userInfo.oneHikId && lib.checkAppModeNotNeutral
      },
      {
        path: '/Support/Store',
        getPermission: () => lib.epPermissions.includes('epartner.customer.map')
      },
      { path: '/installer/basic/batchLicenseQuery' },
      { path: '/installer/basic/operatorsQuery' },
      { path: '/installer/basic/pressureTestSimulator' }
    ]
  ),
  // Order
  ...attachPermission(
    () => lib.userInfo.epSapId,
    [
      { path: '/Order' },
      { path: '/Order/index' },
      {
        path: '/Order/regular',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/single',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/clearance',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/smartTry',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/comboDiscount',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/agingStock',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/search',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/productDetail',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.product')
      },
      {
        path: '/Order/cart/normal',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/cart/clearance',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/cart/smarttry',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/cart/china',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/cart/agingstock',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/productsUpload',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/orderSubmitResult/**',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/orderSubmit/**',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/orderPaying/**',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/paymentResult/**',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/preOrderSubmit/**',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/preOrderSubmitResult/**',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/myQuoteResult',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.place')
      },
      {
        path: '/Order/orderDetail/**',
        getPermission: () => lib.epPermissions.includes('epartner.b2b.order.manage')
      },
      {
        path: '/Order/preOrderDetail/**',
        getPermission: () =>
          lib.epPermissions.includes('epartner.b2b.order.manage')
          && lib.epPermissions.includes('epartner.b2b.order.preorder')
      }
    ]
  ),
  // MyAccount
  // --companyMenuList
  {
    path: '/installer/basic/company',
    getPermission: () => lib.userPermission.companyInfo && lib.userInfo.whiteListUser === 1
  },
  {
    path: '/installer/basic/employees',
    getPermission: () =>
      lib.userPermission.accountAndRole && lib.userPermission.showInFree && lib.userInfo.userLoginMode !== 2 && lib.userPermission.hasEmployee
  },
  {
    path: '/installer/basic/employeeStatistics',
    getPermission: () =>
      lib.userPermission.accountAndRole && lib.userPermission.showInFree && lib.userInfo.whiteListUser === 1
  },
  {
    path: '/installer/basic/role',
    getPermission: () =>
      lib.userPermission.accountAndRole
      && lib.userPermission.showInFree
      && lib.userInfo.userLoginMode !== 2
      && lib.userInfo.whiteListUser === 1
  },
  {
    path: '/MyAccount/agreementManage',
    getPermission: () => lib.epAuthList.dppSignEntrance === 2
  },
  {
    path: '/installer/basic/operationLog',
    getPermission: () => !lib.userInfo.epSessionData?.userSapId && lib.userInfo.whiteListUser === 1
  },
  {
    path: '/installer/basic/coBranding',
    getPermission: () =>
      lib.userPermission.companyInfo
      && lib.userPermission.showInFree
      && lib.userInfo.whiteListUser === 1
      && !lib.userPermission.isCoBrandingAblityReverse
  },
  { path: '/MyAccount/MyAddress', getPermission: () => !lib.isAzureLogin && lib.epPermissions.includes('epartner.basic') && lib.checkAppModeNotNeutral },
  {
    path: '/MyAccount/MyQuote',
    getPermission: () => hasAbilityByOr({ value: '1' }, [137, 138]) && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyOrderQuote',
    getPermission: () => lib.getEpCountrySettings.quoteConvertOrderSwitch && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyForecast',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.forecast') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyPreOrder',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.order.preorder')
  },
  {
    path: '/MyAccount/GiftsVerify',
    getPermission: () =>
      lib.checkAppModeNotNeutral
      && lib.epPermissions.includes('epartner.basic')
      && lib.supportPointSystem
      && lib.userInfo.whiteListUser === 1
  },
  {
    path: '/MyAccount/MyPartnerNetwork',
    getPermission: () => lib.epPermissions.includes('epartner.user.partnernetwork')
  },
  {
    path: '/MyAccount/MyPromotion',
    getPermission: () => lib.epAuthList.proOrderPermission.includes('1')
  },
  //Azure AD 配置
  {
    path: '/installer/basic/configurationAzure',
    getPermission: () => lib.isLegal && lib.checkAppModeNotNeutral
  },
  //账号安全
  {
    path: '/installer/basic/accountSecurity',
    getPermission: () => lib.isLegal && hasCurAbility(273) && lib.userInfo.originType !== 1 && !!lib.userInfo.oneHikId
  },
  // accountMenuList
  { path: '/installer/basic/modifyInformation' },
  {
    path: '/MyAccount/MyFavourite',
    getPermission: () => lib.epPermissions.includes('epartner.basic') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/WaitingList',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.product') && lib.checkAppModeNotNeutral
  },
  {
    path: '/installer/basic/personalizedRecommend',
    getPermission: () => lib.checkAppModeNotNeutral && hasAbilityByOr({ value: '1' }, [78, 80])
  },
  {
    path: '/installer/basic/myTrustDevices',
    getPermission: () => hasCurAbility(273) && !!lib.userInfo.oneHikId && lib.userInfo.originType !== 1 && !lib.userInfo.investigateFlag
  },
  {
    path: '/MyAccount/MyComments',
    getPermission: () => lib.epPermissions.includes('epartner.basic') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyFeedback',
    getPermission: () => lib.epPermissions.includes('epartner.basic') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyIntention',
    getPermission: () => lib.epAuthList.proOrderPermission.includes('0') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/DashBoard',
    getPermission: () => lib.epPermissions.includes('epartner.basic') && lib.checkAppModeNotNeutral
  },
  // orderMenuList
  {
    path: '/MyAccount/MyOrder',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.order.manage') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyReturn',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.orderReturn') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyLogistics',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.logistics') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyInvoice',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.order.manage') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyEWallet',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.ewallet') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/FinancialReport',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.order.financialreport') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/StockList',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.stocklist') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/PriceList',
    getPermission: () => lib.epPermissions.includes('epartner.b2b.pricelist') && lib.checkAppModeNotNeutral
  },
  // rebateMenuList
  {
    path: '/MyAccount/InvitationCode',
    getPermission: () => lib.epPermissions.includes('epartner.hpc.authcode') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/RebateResult',
    getPermission: () =>
      lib.epPermissions.includes('epartner.hpc.rebate') && lib.userPermission.companyInfo && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/RebateEquipmentList',
    getPermission: () => false
  },
  {
    path: '/MyAccount/RebateSetting',
    getPermission: () => lib.epPermissions.includes('epartner.hpc.rebate') && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/RebateDPP',
    getPermission: () =>
      lib.epPermissions.includes('epartner.hpc.rebate')
      && lib.grayscaleFunctions.supportDPP
      && lib.userPermission.companyInfo
      && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyEvent',
    getPermission: () =>
      lib.epPermissions.includes('epartner.basic')
      && hasAbilityByOr({ value: '1' }, [95, 96])
      && lib.checkAppModeNotNeutral
  },
  {
    path: '/MyAccount/MyStore',
    getPermission: () => lib.epAuthList.isEntrance
  },
  // 新工单模块--EP资源，本地无效环境有效-这个路由开关是实时变化的，配置的意义不大，先固定都支持
  {
    path: '/Support/case/submit',
    getPermission: () => true
  },
  {
    path: '/portalAuth',
    getPermission: () => true
  },
  {
    path: '/Support/case/records/**',
    getPermission: () => true
  }
];
